<template>
  <div>

    <locations-list-add-new :is-add-new-location-sidebar-active.sync="isAddNewLocationSidebarActive"
      :role-options="roleOptions" :plan-options="planOptions" @refetch-data="refetchData" />

    <locations-list-edit :is-edit-location-sidebar-active.sync="isEditLocationSidebarActive" :role-options="roleOptions"
      :plan-options="planOptions" :item="item" @refetch-data="refetchData" />

    <!-- Table Container Card -->
    <b-card no-body class="mb-0">

      <div class="m-2">

        <!-- Table Top -->
        <b-row>

          <!-- Per Page -->
          <b-col cols="12" md="6" class="d-flex align-items-center justify-content-start mb-1 mb-md-0">
            <label>Show</label>
            <v-select v-model="limit" :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'" :options="perPageOptions"
              :clearable="false" class="per-page-selector d-inline-block mx-50" />
            <label>entries</label>
          </b-col>

          <!-- Search -->
          <b-col cols="12" md="6">
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input v-model="searchQuery" class="d-inline-block mr-1" placeholder="Search..." />
              <b-button variant="primary" @click="isAddNewLocationSidebarActive = true">
                <span class="text-nowrap">Add Location</span>
              </b-button>
            </div>
          </b-col>
        </b-row>

      </div>

      <b-table ref="refLocationListTable" class="position-relative" :items="fetchLocations" responsive
        :fields="tableColumns" primary-key="id" :sort-by.sync="sortBy" show-empty empty-text="No matching records found"
        :sort-desc.sync="sortOrderSync">
        <template #table-busy>
          <div class="text-center my-3">
            <b-spinner style="width: 3rem; height: 3rem;" variant="primary" label="Loading..."></b-spinner>
          </div>
        </template>
        <!-- Column: Name -->
        <template #cell(name)="data">
          <div class="text-nowrap">
            <span class="align-text-top text-capitalize"><b>{{ data.item.name }}</b></span>
          </div>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <feather-icon icon="EditIcon" class="edit-button" @click="editItem(data.item)" />
        </template>

      </b-table>
      <div class="mx-2 mb-2">
        <b-row>

          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-start">
            <span class="text-muted">Showing {{ dataMeta.from }} to {{ dataMeta.to }} of {{ dataMeta.of }}
              entries</span>
          </b-col>
          <!-- Pagination -->
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-end">

            <b-pagination v-model="page" :total-rows="totalLocations" :per-page="limit" first-number last-number
              class="mb-0 mt-1 mt-sm-0" prev-class="prev-item" next-class="next-item">
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>

          </b-col>

        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormInput, BButton, BTable, BMedia, BAvatar, BLink,
  BBadge, BDropdown, BDropdownItem, BPagination, BSpinner
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import store from '@/store'
import { ref, onUnmounted, computed } from '@vue/composition-api'
import { avatarText } from '@core/utils/filter'
import useLocationsList from './useLocationsList'
import locationStoreModule from '../locationStoreModule'
import LocationsListAddNew from './LocationsListAddNew.vue'
import LocationsListEdit from './LocationsListEdit.vue'

export default {
  components: {
    LocationsListEdit,
    LocationsListAddNew,

    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BSpinner,

    vSelect,
  },
  setup() {
    const LOCATION_APP_STORE_MODULE_NAME = 'app-location'

    // Register module
    if (!store.hasModule(LOCATION_APP_STORE_MODULE_NAME)) store.registerModule(LOCATION_APP_STORE_MODULE_NAME, locationStoreModule)

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(LOCATION_APP_STORE_MODULE_NAME)) store.unregisterModule(LOCATION_APP_STORE_MODULE_NAME)
    })

    const isAddNewLocationSidebarActive = ref(false)
    const isEditLocationSidebarActive = ref(false)
    const item = ref(null)

    const roleOptions = [
      { label: 'Admin', value: 'admin' },
      { label: 'User', value: 'user' },
    ]

    const planOptions = [
      { label: 'Basic', value: 'basic' },
      { label: 'Company', value: 'company' },
      { label: 'Enterprise', value: 'enterprise' },
      { label: 'Team', value: 'team' },
    ]

    const statusOptions = [
      { label: 'Pending', value: 'pending' },
      { label: 'Active', value: 'active' },
      { label: 'Inactive', value: 'inactive' },
    ]

    const {
      fetchLocations,
      tableColumns,
      limit,
      page,
      totalLocations,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      sortOrder,
      refLocationListTable,
      refetchData,

      // UI
      resolveLocationRoleVariant,
      resolveLocationRoleIcon,
      resolveLocationStatusVariant,

      // Extra Filters
      roleFilter,
      planFilter,
      statusFilter,
    } = useLocationsList()

    const sortOrderSync = computed({
      get: () => sortOrder.value === 'desc',
      set: (newVal) => {
        sortOrder.value = newVal ? 'desc' : 'asc'
      }
    })

    return {

      // Sidebar
      isAddNewLocationSidebarActive,
      isEditLocationSidebarActive,

      fetchLocations,
      tableColumns,
      limit,
      page,
      totalLocations,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      sortOrder,
      sortOrderSync,
      refLocationListTable,
      refetchData,

      // Filter
      avatarText,

      // UI
      resolveLocationRoleVariant,
      resolveLocationRoleIcon,
      resolveLocationStatusVariant,

      roleOptions,
      planOptions,
      statusOptions,

      // Extra Filters
      roleFilter,
      planFilter,
      statusFilter,

      // Edit Item
      item
    }
  },
  methods: {
    editItem(item) {
      this.item = item
      this.isEditLocationSidebarActive = true
    }
  }
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.edit-button {
  color: $primary;
  cursor: pointer;
}
</style>
